"use client";

/**
 * AudioReactiveArtwork — a canvas overlay that animates ON TOP of cover artwork
 * in time with the actually-playing track. It taps the global player's Web Audio
 * analyser (analyserL / analyserR from useMediaPlayer) for live frequency data,
 * so the artwork "moves with the sound" — e.g. a speaker-woofer surface pumping
 * to the bass.
 *
 * Drop it as an absolutely-positioned sibling over any artwork:
 *   <div className="relative">
 *     <SlideshowImage ... />
 *     <AudioReactiveArtwork effect="woofer" />
 *   </div>
 *
 * It's pointer-events-none and respects prefers-reduced-motion. Sound only
 * (Gordon 2026-11-04: "stop all effects when music is not playing … the
 * intensity of music should 100% affect the artwork … even the coπ mic
 * signal"): the player while it plays, else a microphone / the Academy synth
 * (micLevel), else nothing is drawn and the artwork shows as it is. One
 * exception: a freshly picked or changed effect shows itself on a demo
 * groove for PREVIEW_MS, then eases out ("so you see what the effect looks
 * like … then it stops until the music starts").
 *
 * Admins / content creators choose the effect per work (stored as `artworkFx`
 * on the work doc) — see ARTWORK_FX below for the catalog.
 */

import { useEffect, useRef } from "react";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { micLevel } from "@/hooks/use-avatar-reactivity";
import { ReadingDelay, visualHoldMs } from "@/lib/visual-sync";

export type ArtworkFxType =
  | "off"
  | "woofer"
  | "spectrum"
  | "spectrum-led"
  | "matrix"
  | "spectrum-wave"
  | "ripple"
  | "glitch"
  | "goniometer"
  | "particles-sparks"
  | "particles-snow"
  | "particles-fireflies"
  | "particles-starfield"
  | "particles-random"
  | "neon";

/** Catalog used by the admin/creator picker. */
export const ARTWORK_FX: { id: ArtworkFxType; label: string; hint: string }[] = [
  { id: "off",                label: "None",                   hint: "No animation" },
  { id: "woofer",              label: "Woofer",                 hint: "Cover zooms in and out with the bass" },
  { id: "spectrum",            label: "Spectrum Line",          hint: "Thin monochrome green frequency graph" },
  { id: "spectrum-led",        label: "Spectrum LED",           hint: "Classic green → amber → red LED analyser bars" },
  { id: "matrix",              label: "Matrix Rain",            hint: "Code glyphs rise to the frequencies" },
  { id: "spectrum-wave",       label: "Spectrum Wave",          hint: "Mirrored filled spectrum, scope-style" },
  { id: "ripple",              label: "Ripple",                 hint: "Water rings ripple out on the kick & snare" },
  { id: "goniometer",          label: "Goniometer",             hint: "Stereo phase scope — mix width & correlation as a live Lissajous pattern" },
  { id: "particles-sparks",    label: "Particles — Sparks",     hint: "Sparks orbit outward and flare on the beat" },
  { id: "particles-snow",      label: "Particles — Snowfall",   hint: "Soft particles drift down and sway with the mix" },
  { id: "particles-fireflies", label: "Particles — Fireflies",  hint: "Glowing dots wander and flicker with the beat" },
  { id: "particles-starfield", label: "Particles — Starfield",  hint: "Streaks warp outward, faster on the bass" },
  { id: "particles-random",    label: "Particles — Random",     hint: "Picks a random particle style for this track" },
  { id: "neon",                label: "Neon Grid",              hint: "Retro synthwave grid pulses with the bass" },
  { id: "glitch",              label: "RGB Glitch",             hint: "Chromatic slices on transients" },
];

/** Old saved `artworkFx` values keep rendering as their closest replacement —
 *  no Firestore migration needed. "vectors" (Pulse Vectors) → Goniometer per
 *  Gordon ("makes more sense musically"); plain "particles" → the sparks
 *  style that was its only look before the particle-style picker existed. */
const LEGACY_FX_ALIAS: Record<string, string> = { vectors: "goniometer", particles: "particles-sparks" };

interface Props {
  effect?: ArtworkFxType;
  /** Multi-select: draw several effects stacked in one frame. Takes precedence
   *  over `effect` when non-empty (Gordon: multi-select FX on the thumbnail). */
  effects?: string[];
  /** 0..1 — global strength multiplier. */
  intensity?: number;
  /** Hex accent; falls back to the theme primary. */
  color?: string;
  className?: string;
  /** Cover image URL — REQUIRED for the "woofer" warp, which physically warps
   *  the underlying artwork (no overlay lines). Other effects ignore it. */
  imageUrl?: string;
}

/** How long a just-picked effect previews itself in silence, and its fade-out. */
const PREVIEW_MS = 4500;
const PREVIEW_FADE_MS = 1000;
/** When the Artwork FX picker last changed something (it fires `bb:artwork-fx`). */
let lastPick = 0;
if (typeof window !== "undefined") window.addEventListener("bb:artwork-fx", () => { lastPick = performance.now(); });

const PREFERS_REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function AudioReactiveArtwork({ effect = "off", effects, intensity = 1, color, className, imageUrl }: Props) {
  // Effects to draw this frame — the multi-select array wins; else the single
  // effect (unless it's "off"). Woofer is drawn first so overlays sit on top.
  const fxList: string[] = ((effects && effects.length)
    ? effects.filter(e => e && e !== "off")
    : (effect && effect !== "off" ? [effect] : [])
  ).map(id => LEGACY_FX_ALIAS[id] || id);
  const fxKey = fxList.join(",");
  const { analyserL, analyserR, isPlaying } = useMediaPlayer();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Load the cover image for the warp pass (no crossOrigin → never blocks on
  // CORS; we only drawImage, never read pixels back, so a tainted canvas is OK).
  useEffect(() => {
    if (!imageUrl) { imgRef.current = null; return; }
    const im = new Image();
    im.decoding = "async";
    im.onload = () => { imgRef.current = im; };
    im.src = imageUrl;
    return () => { imgRef.current = null; };
  }, [imageUrl]);
  // Smoothed energy bands so motion is musical, not jittery.
  const env = useRef({ bass: 0, mid: 0, treble: 0, level: 0, beat: 0, t: 0,
    // Ripple: refractory gates that fire on a kick (bass transient) and a snare
    // (mid/high transient); each fire spawns a slow, fading WATER ring.
    kickPhase: 1, snarePhase: 1,
    // Live pool of expanding water rings ({ born, kind }); drawn + faded each frame.
    rings: [] as { born: number; kind: "k" | "s" }[],
    // Onset detection: FAST follower vs SLOW baseline per drum band. A hit =
    // fast jumping above baseline. Far more sensitive than a frame delta,
    // because the analyser's own smoothing flattens frame-to-frame jumps.
    kickFast: 0, kickSlow: 0, snFast: 0, snSlow: 0,
    // Matrix-rain column state (lazily sized to the canvas width).
    matrix: null as null | { cols: number; y: Float32Array; sp: Float32Array; ch: Uint8Array },
    // Goniometer: raw per-channel time-domain sample buffers (resized to the
    // analyser's fftSize on first use) plus a short trail of recent frames'
    // plotted points (oldest→newest, faded by age) for a phosphor-scope look.
    gonio: { bufL: new Float32Array(0), bufR: new Float32Array(0), trail: [] as number[][] } });

  // A change of effect, strength or colour — or appearing because one was just
  // picked in the picker — previews it for a moment, even in silence.
  const previewUntil = useRef(0);
  const mounted = useRef(false);
  useEffect(() => {
    if (fxList.length && (mounted.current || performance.now() - lastPick < 800)) previewUntil.current = performance.now() + PREVIEW_MS;
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxKey, intensity, color]);

  // Seed for the Particles field (recycled orbit/drift positions) and, when
  // "Random" is picked, which concrete particle style this mount landed on.
  const seed = useRef(makeSeed());

  useEffect(() => {
    if (fxList.length === 0 || PREFERS_REDUCED) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const accent = color || getComputedThemePrimary() || "#FF3366";
    const [ar, ag, ab] = hexToRgb(accent);

    const freq = new Uint8Array(1024);
    const prevLevel = { v: 0 };
    // Readings wait out the speakers' delay, so the picture lands on the beat you hear.
    const delay = new ReadingDelay(1024);
    let silentSince = 0;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const w = canvas.clientWidth, h = canvas.clientHeight;
      if (!w || !h) return;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      const an = analyserL || analyserR;
      const live = !!an && isPlaying;
      const bins = live ? Math.min(freq.length, an!.frequencyBinCount) : 256;
      const mic = micLevel();
      if (live) {
        an!.getByteFrequencyData(freq.subarray(0, bins) as Uint8Array);
        delay.hold(freq, visualHoldMs(an), performance.now(), bins);
        // The microphone too, where it is louder (all sound counts).
        if (mic !== null) for (let i = 0; i < bins; i++) freq[i] = Math.max(freq[i], Math.round(mic * Math.pow(1 - i / bins, 1.2) * 255));
      } else if (mic !== null) {
        // A microphone or the synth: one level, spread like a spectrum (lows strongest).
        for (let i = 0; i < bins; i++) freq[i] = Math.round(mic * Math.pow(1 - i / bins, 1.2) * 255);
      } else if (performance.now() < previewUntil.current) {
        // Just picked: a demo groove (~97 BPM pulse + a wandering spectrum) so
        // the effect shows what it does; it eases out over the last second.
        const fade = Math.min(1, (previewUntil.current - performance.now()) / PREVIEW_FADE_MS);
        const t = env.current.t;
        const beatPhase = (t % 0.62) / 0.62;
        const punch = Math.pow(Math.max(0, 1 - beatPhase * 2.4), 2);
        for (let i = 0; i < bins; i++) {
          const f = i / bins;
          const slope = Math.pow(1 - f, 1.6);
          const wob = 0.55 + 0.45 * Math.sin(t * 2.2 + f * 17) * Math.sin(t * 0.9 + f * 5);
          const kick = f < 0.1 ? punch * 0.9 : 0;
          freq[i] = Math.min(255, Math.max(0, Math.round((slope * wob * 0.62 + kick) * fade * 255)));
        }
        silentSince = 0;
      } else {
        // Silence: let everything settle for a moment, then draw nothing at all.
        freq.fill(0, 0, bins);
        if (!silentSince) silentSince = performance.now();
        if (performance.now() - silentSince > 500) {
          ctx.clearRect(0, 0, w, h);
          env.current.rings.length = 0;
          return;
        }
      }
      if (live || mic !== null) silentSince = 0;

      // Energy bands (normalised 0..1).
      const band = (a: number, b: number) => {
        let s = 0, n = 0;
        for (let i = Math.floor(bins * a); i < Math.floor(bins * b); i++) { s += freq[i]; n++; }
        return n ? s / n / 255 : 0;
      };
      const bass = band(0.0, 0.08);
      const mid = band(0.08, 0.4);
      const treble = band(0.4, 1.0);
      const level = (bass * 1.4 + mid + treble * 0.6) / 3;
      const e = env.current;
      // Asymmetric smoothing: instant attack — on the beat — and a soft release
      // (feels like a real cone; Gordon 2026-11-04: "react instantly").
      const sm = (cur: number, tgt: number) => tgt > cur ? cur + (tgt - cur) * 0.9 : cur + (tgt - cur) * 0.14;
      e.bass = sm(e.bass, bass); e.mid = sm(e.mid, mid); e.treble = sm(e.treble, treble); e.level = sm(e.level, level);
      // Beat = positive jump in overall level.
      const jump = Math.max(0, level - prevLevel.v); prevLevel.v = level;
      e.beat = Math.max(e.beat * 0.9, jump * 4);
      e.t += 0.016;

      // Drum-onset detection for Ripple. A KICK = a jump in the low thump band,
      // a SNARE = a jump in the 2–6 kHz crack band. We track each band with a
      // FAST follower and a SLOW baseline; a hit is the fast value pulling
      // clearly above its baseline. This reacts to real kicks/snares even in a
      // dense mix — the old frame-to-frame delta was killed by analyser
      // smoothing, so it almost never fired.
      const kickBand = band(0.0, 0.05);    // ~0–1 kHz : kick thump
      const snareBand = band(0.10, 0.30);  // ~2–6 kHz : snare crack / body
      e.kickFast += (kickBand - e.kickFast) * 0.55;
      e.kickSlow += (kickBand - e.kickSlow) * 0.06;
      e.snFast   += (snareBand - e.snFast) * 0.55;
      e.snSlow   += (snareBand - e.snSlow) * 0.06;
      const kickExcess = e.kickFast - e.kickSlow;
      const snareExcess = e.snFast - e.snSlow;
      // Sensitive thresholds + a low silence gate; a short refractory window so
      // one hit spawns exactly one ripple. Each fire drops a fresh water ring.
      if (kickExcess > 0.03 && kickBand > 0.06 && e.kickPhase > 0.28) { e.kickPhase = 0; e.rings.push({ born: e.t, kind: "k" }); }
      if (snareExcess > 0.022 && snareBand > 0.035 && e.snarePhase > 0.28) { e.snarePhase = 0; e.rings.push({ born: e.t, kind: "s" }); }
      e.kickPhase = Math.min(1, e.kickPhase + 0.05);
      e.snarePhase = Math.min(1, e.snarePhase + 0.065);
      if (e.rings.length > 18) e.rings.splice(0, e.rings.length - 18); // bound the pool

      const k = intensity;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2;

      // Draw each selected effect, stacked, in order (woofer base first).
      for (const fx of fxList) {
      switch (fx) {
        case "woofer": {
          // Gordon: Woofer should JUST zoom the thumbnail with the audio
          // intensity — no rings, no warp, no other visual. Draw the whole
          // cover uniformly scaled by the bass level.
          const img = imgRef.current;
          const pump = Math.min(1, e.bass * k);
          if (img && img.width) {
            const zoom = 1 + pump * 0.32;                 // up to +32% on a bass hit
            const sBase = Math.max(w / img.width, h / img.height) * zoom;
            const dw = img.width * sBase, dh = img.height * sBase;
            ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
          }
          // No image → leave the canvas transparent (the underlying artwork
          // shows through unchanged); nothing else is drawn.
          break;
        }
        case "particles-sparks":
        case "particles-snow":
        case "particles-fireflies":
        case "particles-starfield":
        case "particles-random": {
          // Several distinct particle looks sharing one recycled point pool
          // (s.particles) — each style just reinterprets the same p.a/p.r/
          // p.sp/p.sz fields differently. "Random" picks one concrete style
          // once per mount (s.particleStyle) and renders identically to it —
          // Gordon: "more different style of particles... or random."
          const s = seed.current;
          const style = fx === "particles-random"
            ? (s.particleStyle || (s.particleStyle = randomParticleStyle()))
            : fx.slice("particles-".length);
          ctx.globalCompositeOperation = "screen";
          const flare = 0.6 + e.beat * 1.4 * k;

          if (style === "snow") {
            // p.a = x fraction (0..1), p.r = y fraction (0..1) — drifts DOWN
            // with a gentle sway, recycles to the top.
            for (const p of s.particles) {
              p.r += (0.0009 + p.sp * 0.0022) * (0.5 + e.level * 2.2);
              if (p.r > 1.05) { p.r = -0.05; p.a = Math.random(); }
              const sway = Math.sin(p.a * 30 + e.t * (0.6 + p.sp)) * 0.02 * (1 + e.mid);
              const x = (p.a + sway) * w, y = p.r * h;
              const sz = Math.max(0.5, p.sz * (0.8 + e.bass * 1.6 * k));
              ctx.beginPath(); ctx.arc(x, y, sz, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(255,255,255,${(0.5 + 0.4 * k).toFixed(3)})`;
              ctx.fill();
            }
          } else if (style === "fireflies") {
            // p.a = wander phase — slow organic drift in a loose orbit, each
            // particle flickering independently (own phase from p.sz).
            for (const p of s.particles) {
              p.a += 0.006 * p.sp * (1 + e.mid);
              const wobbleR = 0.25 + 0.5 * ((Math.sin(p.a * 0.7) + 1) / 2);
              const ang = p.a * 1.3 + Math.cos(p.a * 0.5) * 2;
              const x = cx + Math.cos(ang) * wobbleR * R;
              const y = cy + Math.sin(ang) * wobbleR * R * 0.9;
              const flicker = clamp(0.35 + 0.6 * ((Math.sin(p.a * 4 + p.sz) + 1) / 2) + e.beat * 0.5 * k, 0, 1);
              ctx.beginPath(); ctx.arc(x, y, Math.max(0.6, p.sz * 0.9), 0, Math.PI * 2);
              ctx.fillStyle = `rgba(${ar},${ag},${ab},${flicker.toFixed(3)})`;
              ctx.fill();
            }
          } else if (style === "starfield") {
            // Streaks racing outward from centre — a short trailing line
            // instead of a dot reads as speed/motion-blur, hyperspace-style.
            for (const p of s.particles) {
              p.r += (0.006 + p.sp * 0.012) * (0.5 + e.level * 3 + e.bass * 2 * k);
              if (p.r > 1.05) { p.r = 0.02; p.a = Math.random() * Math.PI * 2; }
              const rr = p.r * R * 1.1;
              const x = cx + Math.cos(p.a) * rr, y = cy + Math.sin(p.a) * rr;
              const prevRR = Math.max(0, rr - (4 + p.sp * 8));
              const px = cx + Math.cos(p.a) * prevRR, py = cy + Math.sin(p.a) * prevRR;
              ctx.strokeStyle = `rgba(255,255,255,${((0.3 + p.r * 0.6) * (0.5 + 0.5 * k)).toFixed(3)})`;
              ctx.lineWidth = Math.max(0.6, p.sz * 0.6);
              ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(x, y); ctx.stroke();
            }
          } else {
            // sparks (default / legacy "particles") — orbit outward from the
            // centre, speeding up with overall energy, flaring on the beat.
            for (const p of s.particles) {
              p.r += (0.0018 + p.sp * 0.004) * (0.4 + e.level * 2.6);
              p.a += 0.004 * p.sp * (1 + e.mid * 2.2);
              if (p.r > 1.05) { p.r = 0.015; p.a = Math.random() * Math.PI * 2; }
              const rr = p.r * R * 1.05;
              const x = cx + Math.cos(p.a) * rr;
              const y = cy + Math.sin(p.a) * rr;
              const sz = Math.max(0.4, p.sz * (1 + e.bass * 2.4 * k) * flare);
              const alpha = (1 - p.r * 0.9) * (0.45 + 0.55 * k);
              ctx.beginPath(); ctx.arc(x, y, sz, 0, Math.PI * 2);
              ctx.fillStyle = p.sz > 2.1
                ? `rgba(255,255,255,${alpha.toFixed(3)})`
                : `rgba(${ar},${ag},${ab},${alpha.toFixed(3)})`;
              ctx.fill();
            }
          }
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "neon": {
          // Retro synthwave: a perspective grid receding to a horizon, with a
          // glowing sun that pulses on the bass. Very recognisable, and it
          // reads as "music" instantly.
          ctx.globalCompositeOperation = "screen";
          const horizon = cy * (0.92 - e.bass * 0.08 * k);
          const scroll = (e.t * (0.35 + e.level * 0.9)) % 1;
          const line = `rgba(${ar},${ag},${ab},${(0.3 + e.level * 0.45).toFixed(3)})`;
          ctx.strokeStyle = line;
          ctx.lineWidth = 1 + e.bass * 2.2 * k;
          // Horizontal lines: perspective spacing (f² pushes them apart lower down).
          const rows = 11;
          for (let i = 0; i < rows; i++) {
            const f = (i + scroll) / rows;
            const y = horizon + (h - horizon) * (f * f);
            ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
          }
          // Vertical lines fanning out from the vanishing point.
          const cols = 12;
          for (let i = 0; i <= cols; i++) {
            const fx = i / cols - 0.5;
            ctx.beginPath();
            ctx.moveTo(cx + fx * w * 0.06, horizon);
            ctx.lineTo(cx + fx * w * 1.7, h);
            ctx.stroke();
          }
          // Just the grid — no glowing sun/orb (Gordon: "just the grid").
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "ripple": {
          // Water ripples: each kick/snare drops a ring that expands with an
          // ease-OUT (fast then slowing, like a real splash) and fades away
          // smoothly over a couple of seconds. Rings overlap like rain on water.
          ctx.globalCompositeOperation = "screen";
          const rings = e.rings;
          for (let idx = rings.length - 1; idx >= 0; idx--) {
            const ring = rings[idx];
            const isKick = ring.kind === "k";
            const life = isKick ? 2.4 : 1.7;              // kick rings linger longer
            const age = e.t - ring.born;
            if (age >= life) { rings.splice(idx, 1); continue; }
            const p = age / life;                          // 0..1
            const ease = 1 - Math.pow(1 - p, 2);           // ease-out radius (water)
            const fade = Math.pow(1 - p, 1.7);             // smooth fade to zero
            const rr = (isKick ? R * 1.18 : R * 0.82) * ease;
            const baseA = (isKick ? 0.5 : 0.42) * (0.5 + 0.6 * k) * fade;
            const cr = isKick ? ar : 255, cg = isKick ? ag : 255, cb = isKick ? ab : 255;
            // Bright leading crest.
            ctx.lineWidth = (isKick ? 3 : 2) * (1 - p * 0.5);
            ctx.strokeStyle = `rgba(${cr},${cg},${cb},${baseA.toFixed(3)})`;
            ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2); ctx.stroke();
            // Faint trailing crest just inside — the "double ripple" water feel.
            ctx.lineWidth = 1.4 * (1 - p * 0.5);
            ctx.strokeStyle = `rgba(${cr},${cg},${cb},${(baseA * 0.45).toFixed(3)})`;
            ctx.beginPath(); ctx.arc(cx, cy, rr * 0.8, 0, Math.PI * 2); ctx.stroke();
          }
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "spectrum": {
          // Thin monochrome green line graph — a clean, vectorised spectrum curve
          // with a soft neon glow.
          ctx.globalCompositeOperation = "screen";
          const n = 110, step = Math.max(1, Math.floor((bins * 0.72) / n));
          ctx.beginPath();
          for (let i = 0; i < n; i++) {
            const v = (freq[i * step] / 255) * k;
            const x = (i / (n - 1)) * w;
            const y = h - v * h * 0.88;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.strokeStyle = `rgba(60,243,127,${(0.55 + 0.4 * e.level).toFixed(3)})`;
          ctx.lineWidth = 1.5;
          ctx.shadowColor = "rgba(60,243,127,0.7)";
          ctx.shadowBlur = 6;
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "spectrum-led": {
          // Classic hardware analyser: segmented LED bars, green → amber → red.
          ctx.globalCompositeOperation = "screen";
          const n = 26, seg = 15, bw = w / n;
          const step = Math.max(1, Math.floor((bins * 0.72) / n));
          const segH = h / seg;
          for (let i = 0; i < n; i++) {
            const v = Math.min(1, (freq[i * step] / 255) * k * 1.1);
            const lit = Math.round(v * seg);
            for (let s = 0; s < seg; s++) {
              const frac = s / seg;
              const col = frac > 0.82 ? [255, 70, 70] : frac > 0.58 ? [255, 200, 45] : [60, 243, 127];
              const a = s < lit ? 0.9 - frac * 0.2 : 0.06;
              ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${a.toFixed(3)})`;
              ctx.fillRect(i * bw + bw * 0.14, h - (s + 1) * segH + 1, bw * 0.72, segH - 2);
            }
          }
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "matrix": {
          // Upside-down "Matrix" rain: katakana glyphs stream UPWARD, each
          // column's speed + brightness driven by its frequency band.
          const glyphW = 12;
          const cols = Math.max(6, Math.floor(w / glyphW));
          let mx = e.matrix;
          if (!mx || mx.cols !== cols) {
            mx = { cols, y: new Float32Array(cols), sp: new Float32Array(cols), ch: new Uint8Array(cols) };
            for (let i = 0; i < cols; i++) { mx.y[i] = Math.random() * h; mx.sp[i] = 0.5 + Math.random(); mx.ch[i] = Math.floor(Math.random() * 90); }
            e.matrix = mx;
          }
          // A translucent dark wash makes the rain READ on bright artwork —
          // the old screen-blended pale glyphs vanished on light covers
          // (screen can only brighten; over white it drew nothing).
          ctx.fillStyle = `rgba(0,10,4,${(0.28 + e.level * 0.1).toFixed(3)})`;
          ctx.fillRect(0, 0, w, h);
          ctx.font = `bold ${glyphW + 2}px monospace`;
          ctx.textAlign = "center";
          const step = Math.max(1, Math.floor((bins * 0.72) / cols));
          for (let i = 0; i < cols; i++) {
            const v = freq[i * step] / 255;
            mx.y[i] -= (0.5 + v * 6 + mx.sp[i]) * k;               // rise; faster where loud
            if (mx.y[i] < -glyphW) { mx.y[i] = h + Math.random() * 40; mx.ch[i] = Math.floor(Math.random() * 90); }
            if (Math.random() < 0.08) mx.ch[i] = (mx.ch[i] + 1) % 90;
            const x = i * glyphW + glyphW / 2;
            const a = Math.min(1, 0.45 + v * 0.55);
            // Bright leading "head" glyph with a glow, then a fading trail.
            ctx.shadowColor = "rgba(120,255,140,0.8)";
            ctx.shadowBlur = 6;
            ctx.fillStyle = `rgba(210,255,215,${a.toFixed(3)})`;
            ctx.fillText(String.fromCharCode(0x30a0 + (mx.ch[i] % 90)), x, mx.y[i]);
            ctx.shadowBlur = 0;
            ctx.fillStyle = `rgba(90,235,120,${(a * 0.7).toFixed(3)})`;
            ctx.fillText(String.fromCharCode(0x30a0 + ((mx.ch[i] + 4) % 90)), x, mx.y[i] + glyphW + 2);
            ctx.fillStyle = `rgba(60,190,95,${(a * 0.4).toFixed(3)})`;
            ctx.fillText(String.fromCharCode(0x30a0 + ((mx.ch[i] + 9) % 90)), x, mx.y[i] + (glyphW + 2) * 2);
          }
          ctx.textAlign = "start";
          break;
        }
        case "spectrum-wave": {
          // Mirrored filled spectrum around the centre line — an oscilloscope-ish
          // "butterfly" with an accent→green→accent gradient.
          ctx.globalCompositeOperation = "screen";
          const n = 84, step = Math.max(1, Math.floor((bins * 0.72) / n)), mid = h / 2;
          const grad = ctx.createLinearGradient(0, 0, w, 0);
          grad.addColorStop(0, `rgba(${ar},${ag},${ab},0.75)`);
          grad.addColorStop(0.5, "rgba(60,243,127,0.75)");
          grad.addColorStop(1, `rgba(${ar},${ag},${ab},0.75)`);
          ctx.fillStyle = grad;
          ctx.globalAlpha = 0.55 + e.level * 0.4;
          ctx.beginPath();
          ctx.moveTo(0, mid);
          for (let i = 0; i < n; i++) { const v = (freq[i * step] / 255) * k; ctx.lineTo((i / (n - 1)) * w, mid - v * mid * 0.9); }
          for (let i = n - 1; i >= 0; i--) { const v = (freq[i * step] / 255) * k; ctx.lineTo((i / (n - 1)) * w, mid + v * mid * 0.9); }
          ctx.closePath();
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.globalCompositeOperation = "source-over";
          break;
        }
        case "glitch": {
          // RGB glitch — when the cover image is available, actually SLICE the
          // artwork: horizontal bands shifted sideways on transients with
          // chromatic (red/cyan) ghost copies. The old version only drew faint
          // accent bars, which read as "does nothing" (Gordon).
          const img = imgRef.current;
          const amt = Math.min(1, e.beat * 2.2 * k + e.level * 0.12);
          if (img && img.width) {
            const sBase = Math.max(w / img.width, h / img.height);
            const dw = img.width * sBase, dh = img.height * sBase;
            const ox = (w - dw) / 2, oy = (h - dh) / 2;
            const slices = 9;
            for (let i = 0; i < slices; i++) {
              const sy = (i / slices) * h;
              const sh = h / slices;
              // Each slice gets its own jitter; mostly calm, violent on beats.
              const jit = Math.sin(e.t * 9 + i * 3.7) * amt * w * 0.09
                        + Math.sin(e.t * 23 + i * 13) * amt * w * 0.03;
              // Source rect in image space for this horizontal band.
              const srcY = ((sy - oy) / dh) * img.height;
              const srcH = (sh / dh) * img.height;
              if (srcY < 0 || srcY + srcH > img.height) continue;
              // Chromatic ghosts: red left, cyan right, then the clean slice.
              if (amt > 0.05) {
                ctx.globalCompositeOperation = "screen";
                ctx.globalAlpha = Math.min(0.55, amt);
                ctx.filter = "saturate(3) hue-rotate(-40deg)";
                ctx.drawImage(img, 0, srcY, img.width, srcH, ox + jit - amt * 10, sy, dw, sh);
                ctx.filter = "saturate(3) hue-rotate(140deg)";
                ctx.drawImage(img, 0, srcY, img.width, srcH, ox + jit + amt * 10, sy, dw, sh);
                ctx.filter = "none";
                ctx.globalAlpha = 1;
                ctx.globalCompositeOperation = "source-over";
              }
              ctx.drawImage(img, 0, srcY, img.width, srcH, ox + jit, sy, dw, sh);
            }
            // Occasional thin scanline flash.
            if (amt > 0.3) {
              ctx.fillStyle = `rgba(255,255,255,${(amt * 0.12).toFixed(3)})`;
              ctx.fillRect(0, (Math.sin(e.t * 17) * 0.5 + 0.5) * h, w, 2);
            }
          } else {
            // No image handed in — legacy accent bars on transients.
            if (amt > 0.04) {
              ctx.globalCompositeOperation = "screen";
              const slices = 6;
              for (let i = 0; i < slices; i++) {
                const y = (Math.sin(e.t * 7 + i) * 0.5 + 0.5) * h;
                const sh = h * 0.04;
                const dx = (Math.sin(e.t * 13 + i)) * amt * w * 0.06;
                ctx.fillStyle = `rgba(${ar},${ag},${ab},${(amt * 0.5).toFixed(3)})`;
                ctx.fillRect(dx, y, w, sh);
              }
              ctx.globalCompositeOperation = "source-over";
            }
          }
          break;
        }
        case "goniometer": {
          // Stereo phase scope — the classic mix-engineer display: a mono/
          // centred signal draws a vertical line, hard-panned content leans
          // left/right, and out-of-phase material spreads wide. X = (L−R),
          // Y = (L+R), each ×cos45° so a full-scale mono signal fills the
          // circle rather than a diamond. This replaced "Pulse Vectors"
          // (Gordon: "makes more sense musically") — it's a genuine read of
          // the mix's width/correlation instead of decorative math shapes,
          // and needs BOTH channels' raw time-domain samples at once, unlike
          // every other style here which shares the single mono `freq` FFT
          // array computed above.
          {
            const gs = e.gonio;
            const n = (live && analyserL && analyserR) ? Math.min(analyserL.fftSize, 1024) : 512;
            if (gs.bufL.length !== n) { gs.bufL = new Float32Array(n); gs.bufR = new Float32Array(n); }
            if (live && analyserL && analyserR) {
              analyserL.getFloatTimeDomainData(gs.bufL);
              analyserR.getFloatTimeDomainData(gs.bufR);
            } else {
              // Idle preview: a slowly widening/narrowing Lissajous so the
              // scope shows SOMETHING before playback starts.
              const t = e.t;
              const phase = 0.5 + 0.45 * Math.sin(t * 0.6);
              const amp = 0.35 + 0.18 * Math.sin(t * 1.3);
              for (let i = 0; i < n; i++) {
                const s = i * 0.085 + t * 3.1;
                gs.bufL[i] = Math.sin(s) * amp;
                gs.bufR[i] = Math.sin(s + phase) * amp * 0.95;
              }
            }

            ctx.globalCompositeOperation = "screen";
            const rot45 = Math.SQRT1_2;
            const scale = R * 0.9 * (0.55 + k * 0.45);

            // Faint diamond + axes so the scope reads even at low signal.
            ctx.strokeStyle = `rgba(${ar},${ag},${ab},0.12)`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx - R * 0.9, cy); ctx.lineTo(cx + R * 0.9, cy);
            ctx.moveTo(cx, cy - R * 0.9); ctx.lineTo(cx, cy + R * 0.9);
            ctx.stroke();

            // Downsample this frame's samples into the trail buffer — plotting
            // all 1024 points/frame is unnecessary and the trail (several
            // frames kept, faded by age) reads better than a single dense one.
            const stride = Math.max(1, Math.floor(n / 200));
            const framePts: number[] = [];
            for (let i = 0; i < n; i += stride) {
              const l = gs.bufL[i], r = gs.bufR[i];
              framePts.push(cx + (l - r) * rot45 * scale, cy - (l + r) * rot45 * scale);
            }
            gs.trail.push(framePts);
            if (gs.trail.length > 5) gs.trail.shift();

            for (let fi = 0; fi < gs.trail.length; fi++) {
              const pts = gs.trail[fi];
              const age = (fi + 1) / gs.trail.length; // 0..1, 1 = newest
              const alpha = (0.06 + age * 0.55 * k).toFixed(3);
              ctx.fillStyle = `rgba(${ar},${ag},${ab},${alpha})`;
              for (let i = 0; i < pts.length; i += 2) {
                ctx.beginPath();
                ctx.arc(pts[i], pts[i + 1], 1.4, 0, Math.PI * 2);
                ctx.fill();
              }
            }
            ctx.globalCompositeOperation = "source-over";
          }
          break;
        }
      } // end switch
      } // end for-each selected effect
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [fxKey, intensity, color, analyserL, analyserR, isPlaying]);

  if (fxList.length === 0 || PREFERS_REDUCED) return null;
  return (
    <canvas
      ref={canvasRef}
      data-artwork-fx=""
      aria-hidden
      className={className}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
    />
  );
}

// ─── Particle field state ──────────────────────────────────────────────────

interface Particle { a: number; r: number; sp: number; sz: number; }
interface Seed {
  particles: Particle[];
  /** Resolved once per mount when "Particles — Random" is selected. */
  particleStyle?: string;
}

const PARTICLE_STYLES = ["sparks", "snow", "fireflies", "starfield"];

/** Pick a random concrete particle style for "Particles — Random". */
function randomParticleStyle(): string {
  return PARTICLE_STYLES[Math.floor(Math.random() * PARTICLE_STYLES.length)];
}

/** Fresh random seed for the particle field (once per mount). */
function makeSeed(): Seed {
  return {
    particles: Array.from({ length: 64 }, () => ({
      a: Math.random() * Math.PI * 2,
      r: Math.random(),
      sp: 0.3 + Math.random() * 0.9,
      sz: 0.8 + Math.random() * 2.4,
    })),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace("#", "");
  const n = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const int = parseInt(n, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function getComputedThemePrimary(): string | null {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
    if (!v) return null;
    // --primary is often "H S% L%" HSL triplet; let the browser resolve it.
    const probe = document.createElement("span");
    probe.style.color = v.includes("%") ? `hsl(${v})` : v;
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe).color;
    probe.remove();
    const m = rgb.match(/\d+/g);
    if (m && m.length >= 3) {
      return "#" + [m[0], m[1], m[2]].map((x) => (+x).toString(16).padStart(2, "0")).join("");
    }
  } catch { /* fall through */ }
  return null;
}
